import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useStore } from '@nanostores/react';
import { toast } from 'react-toastify';
import { $isAuthenticated } from '~/lib/stores/auth';
import { $showAuthModal } from '~/lib/stores/authModal';
import { apiGetSharedProject, apiForkProject } from '~/lib/api/projects';
import type { SharedProject } from '@shared/types/project';

export default function SharedProjectPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isAuthenticated = useStore($isAuthenticated);
  const [forking, setForking] = useState(false);

  const {
    data: project,
    isLoading,
    error,
  } = useQuery<SharedProject>({
    queryKey: ['shared-project', id],
    queryFn: () => apiGetSharedProject(id!),
    enabled: !!id,
  });

  async function handleFork() {
    if (!isAuthenticated) {
      $showAuthModal.set(true);
      return;
    }

    setForking(true);

    try {
      const forked = await apiForkProject(id!);
      toast.success('Project forked successfully');
      navigate(`/project/${forked.id}`);
    } catch {
      toast.error("Couldn't duplicate that project.");
    } finally {
      setForking(false);
    }
  }

  if (isLoading) {
    return (
      <div className="ui-root min-h-dvh bg-page flex items-center justify-center">
        <p className="text-secondary" style={{ fontSize: 13 }}>
          Loading…
        </p>
      </div>
    );
  }

  if (error || !project) {
    return (
      <div className="ui-root min-h-dvh bg-page flex items-center justify-center px-4">
        <div className="text-center max-w-[380px]">
          <span className="i-ph:eye-slash text-[32px] text-muted block mx-auto mb-3" />
          <h2 className="ui-serif text-primary" style={{ fontSize: 24, margin: '0 0 6px' }}>
            Project not found.
          </h2>
          <p className="text-secondary mb-5" style={{ fontSize: 13.5, lineHeight: 1.6 }}>
            This project may not exist or is no longer public.
          </p>
          <Link to="/" className="ui-btn-accent">
            Go home
          </Link>
        </div>
      </div>
    );
  }

  const shareUrl = `${typeof window !== 'undefined' ? window.location.host : 'dunea.app'}/shared/project/${project.id}`;

  return (
    <div className="ui-root min-h-dvh bg-page flex flex-col">
      {/* Viewer chrome bar — the URL chip shrinks so Remix never leaves the screen */}
      <div className="flex items-center gap-2.5 bg-raised border-b border-hairline" style={{ padding: '10px 14px' }}>
        <div
          className="inline-flex min-w-0 items-center gap-2 bg-sunken border border-hairline text-secondary"
          style={{ padding: '6px 10px', borderRadius: 6, fontSize: 11.5, flex: '0 1 auto' }}
        >
          <span className="i-ph:lock-simple shrink-0 text-accent" style={{ fontSize: 11 }} />
          <span className="ui-mono min-w-0 truncate sm:max-w-[260px]">{shareUrl}</span>
          <span className="i-ph:arrow-square-out hidden shrink-0 text-muted sm:inline-block" style={{ fontSize: 11 }} />
        </div>
        <div className="flex-1" />
        <span className="text-muted hidden sm:inline" style={{ fontSize: 11 }}>
          Read-only preview
          {project.ownerDisplayName ? ` · shared by ${project.ownerDisplayName}` : ''}
        </span>
        <button
          type="button"
          onClick={handleFork}
          disabled={forking}
          className="ui-btn-secondary shrink-0 whitespace-nowrap"
          style={{ padding: '6px 12px', fontSize: 11.5 }}
        >
          {forking ? (
            <>
              <span className="i-ph:spinner-gap animate-spin text-[13px]" />
              Remixing...
            </>
          ) : (
            <>
              <span className="i-ph:git-fork text-[13px]" />
              {isAuthenticated ? 'Remix in Dunea' : 'Sign in to remix'}
            </>
          )}
        </button>
      </div>

      {/* Body — project content */}
      <div className="flex-1 relative overflow-y-auto">
        <div className="max-w-3xl mx-auto px-4 pt-8 pb-20 sm:py-12">
          {/* Header */}
          <div className="mb-8">
            <h1
              className="ui-serif text-primary break-words text-[30px] sm:text-[40px]"
              style={{ lineHeight: 1.05, letterSpacing: '-0.02em' }}
            >
              {project.name}
            </h1>
            {project.ownerDisplayName && (
              <p className="mt-2 text-muted" style={{ fontSize: 12 }}>
                by {project.ownerDisplayName}
              </p>
            )}
            {project.description && (
              <p className="mt-3 text-secondary" style={{ fontSize: 14, lineHeight: 1.6 }}>
                {project.description}
              </p>
            )}

            {project.forkedFrom && (
              <p className="mt-3 text-muted" style={{ fontSize: 11.5 }}>
                Forked from{' '}
                <Link
                  to={`/shared/project/${project.forkedFrom}`}
                  className="text-accent hover:brightness-110 transition-[filter]"
                >
                  another project
                </Link>
              </p>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-muted" style={{ fontSize: 11.5 }}>
              <span>
                {project.chatCount} {project.chatCount === 1 ? 'chat' : 'chats'}
              </span>
              <span>Created {new Date(project.createdAt).toLocaleDateString()}</span>
            </div>
          </div>

          {/* Chat list */}
          <div>
            <h2 className="ui-caption mb-4">Chats</h2>

            {project.chats.length === 0 ? (
              <div className="text-center py-12 rounded-[10px] border border-hairline bg-raised">
                <span className="i-ph:chat-circle-dots text-[32px] text-muted block mx-auto mb-3" />
                <p className="text-secondary" style={{ fontSize: 13 }}>
                  No chats in this project yet
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {project.chats.map((chat, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-[10px] border border-hairline bg-raised"
                    style={{ padding: '12px 14px' }}
                  >
                    <span className="flex-1 min-w-0 text-primary truncate" style={{ fontSize: 13 }}>
                      {chat.description || 'Untitled chat'}
                    </span>
                    <span className="text-muted ml-4" style={{ fontSize: 11.5 }}>
                      {new Date(chat.updatedAt).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Floating "Made with Dunea" badge */}
        <div
          className="fixed flex items-center gap-1.5 bg-page/85 border border-subtle text-secondary backdrop-blur-[6px]"
          style={{
            bottom: 'calc(14px + env(safe-area-inset-bottom))',
            right: 'calc(14px + env(safe-area-inset-right))',
            padding: '5px 10px',
            borderRadius: 999,
            fontSize: 11,
          }}
        >
          <span className="i-ph:sparkle text-accent" style={{ fontSize: 11 }} />
          Made with Dunea
        </div>
      </div>
    </div>
  );
}
